import { apiClient } from '../../../core/api/index.js';
import { CDN_URL, WORKER_URL } from '../../../js/config.js';
import { HangoutState } from '../state/HangoutState.js';
import { normalizeGender } from '../../../js/utils/genderUtils.js';
import { storage } from '../../../js/utils/storage.js';
import { getSession, getCurrentUserId } from '../../../js/utils/auth_utils.js';
import { fetchPrivateCharacters } from '../../../js/utils/privateCharactersApi.js';
import { filterScenariosForSfw } from '../../../js/utils/nsfwTags.js';
import { getNsfwEnabled } from '../../../js/utils/userPrefs.js';
import { fetchCache } from '../../../js/utils/fetchCache.js';
import { normalizeLanguageCode } from '../../../js/utils/languageData.js';
import { getCurrentLanguage } from '../../../js/translations.js';
import { getBackgroundService } from '../../../core/services/BackgroundService.js';
import {
  ensureBlockedContentLoaded,
  filterBlockedCharacters,
  filterBlockedScenarios,
  getBlockedFingerprint
} from '../../moderation/blockedContentStore.js';

const getCharacterInfo = (character) => (
  character?.info && typeof character.info === 'object' && !Array.isArray(character.info)
    ? character.info
    : {}
);

export class HangoutService {
  static #instance = null;

  static getInstance() {
    if (!HangoutService.#instance) {
      HangoutService.#instance = new HangoutService();
    }
    return HangoutService.#instance;
  }

  constructor(apiClientInstance = apiClient) {
    if (HangoutService.#instance) {
      return HangoutService.#instance;
    }

    this.api = apiClientInstance;
    this.pendingRequests = new Map();
    this.cache = new Map();
    this.cacheTTL = 5 * 60 * 1000;

    document.addEventListener('mappingsUpdated', this.handleMappingsUpdated.bind(this));

    HangoutService.#instance = this;
  }

  _getActiveLanguage() {
    return normalizeLanguageCode(getCurrentLanguage() || storage.getLanguage());
  }

  _mapCharacterRecord(c, id = c?.id) {
    const info = getCharacterInfo(c);
    const isPrivate = !!(c?.private || c?.rank === 'Private');
    return {
      id,
      name: c.name,
      voice_type: c.voice || null,
      voice_id: c.voice_id || null,
      description: info.description ?? null,
      info,
      total_messages_sent: c.total_messages_sent || c.pop,
      total_popularity: c.pop,
      sprites: Number(c.sprites) || 0,
      creator_user_id: c.creator_user_id || c.user,
      updated_at: c.updated_at || null,
      gender: normalizeGender(c),
      private: isPrivate,
      image: c.image || c.image_url || null,
      rank: isPrivate ? 'Private' : (c.rank ?? null),
      ce: c.ce ?? null,
      cen: c.cen ?? null
    };
  }

  async _normalizeScenarioDetails(s, resolvedLoc) {
    const spotImageUrl = await this.getSpotImageUrl(resolvedLoc);
    return {
      id: s.id.toString(),
      name: s.name,
      description: s.descr,
      image: spotImageUrl,
      background: resolvedLoc,
      outfits: s.chars?.[0]?.family || [],
      loc_id: resolvedLoc,
      loc: resolvedLoc,
      time: s.time,
      scenes: [],
      allCharacters: (s.chars || []).map(char => ({
        id: char.id,
        family: char.family,
        outfit: char.outfit,
        goal: char.goal || null,
        gender: char.gender || null,
        name: char.name || null
      })),
      sprite_url: s.url || '',
      visibility: s.visibility || 'public',
      tags: s.tags || []
    };
  }

  _upsertScenarioInState(scenarioData) {
    HangoutState.getInstance().updateScenario(scenarioData);
  }

  async getSpotImageUrl(locId) {
    if (!locId) return null;

    const locIdStr = typeof locId === 'string' ? locId : String(locId);
    if (!/\.([123])$/.test(locIdStr)) return null;

    const webpUrl = `${CDN_URL}/images/spots/${locId}.webp`;
    const pngUrl = `${CDN_URL}/images/spots/${locId}.png`;

    try {
      const response = await fetch(webpUrl, { method: 'HEAD' });
      if (response.ok) return webpUrl;
    } catch {
      console.warn(`Webp not available for spot ${locId}, falling back to png`);
    }

    return pngUrl;
  }

  async getAvailableCharacters(options = {}) {
    const cacheKey = `getAvailableCharacters:${getCurrentUserId() || 'anon'}:${getBlockedFingerprint()}:${JSON.stringify(options)}`;
    return this._makeRequest(cacheKey, () => this._getAvailableCharactersImpl());
  }

  async _getAvailableCharactersImpl() {
    await ensureBlockedContentLoaded();
    const rankingsData = await fetchCache.fetchCharacterRankings();
    const publicChars = filterBlockedCharacters((rankingsData || []).map((c) => this._mapCharacterRecord(c)));
    const seenIds = new Set(publicChars.map((c) => String(c.id)));

    if (!getCurrentUserId()) return publicChars;

    let privateList = [];
    try {
      privateList = await fetchPrivateCharacters();
    } catch (error) {
      console.warn('[HangoutService] Failed to fetch private characters:', error);
    }

    const privateChars = (privateList || [])
      .map((c) => this._mapCharacterRecord({ ...c, private: true, rank: 'Private' }))
      .filter((c) => c.id != null && !seenIds.has(String(c.id)));

    return filterBlockedCharacters([...publicChars, ...privateChars]);
  }

  async getCharacterDetails(characterId, options = {}) {
    const language = this._getActiveLanguage();
    const cacheKey = `getCharacterDetails:${characterId}:${language}:${JSON.stringify(options)}`;
    return this._makeRequest(cacheKey, async () => {
      const c = await fetchCache.fetchCharacterDetails(characterId, null, language);
      if (!c) throw new Error(`Character ${characterId} not found in public or private data`);
      return this._mapCharacterRecord(c, characterId);
    });
  }

  getBackgroundUrl(loc_id) {
    if (!loc_id) return `${CDN_URL}/images/backgrounds/default.webp`;
    const timeSlot = HangoutState.getInstance().getCurrentTimeSlot();
    return getBackgroundService().constructBackgroundUrl(Math.floor(Number(loc_id)), timeSlot);
  }

  async getScenarios(characterId, options = {}) {
    const cacheKey = `getScenarios:${characterId}:${JSON.stringify(options)}:${getNsfwEnabled() ? 'unrestricted' : 'filtered'}:${getBlockedFingerprint()}`;
    return this._makeRequest(cacheKey, () => this._getScenariosImpl(characterId, options));
  }

  async _getScenariosImpl(characterId, options = {}) {
    let scenarios = await this._fetchScenarios();
    await ensureBlockedContentLoaded();
    scenarios = filterBlockedScenarios(scenarios);

    if (options.filter) {
      const filter = options.filter.toLowerCase();
      scenarios = scenarios.filter(s => s.name.toLowerCase().includes(filter));
    }

    if (options.sort === 'name') {
      scenarios.sort((a, b) => a.name.localeCompare(b.name));
    }

    const offset = options.offset || 0;
    const limit = options.limit || 20;
    return scenarios.slice(offset, offset + limit);
  }

  async _makeRequest(cacheKey, requestFn) {
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (cached.expiry > Date.now()) return cached.data;
      this.cache.delete(cacheKey);
    }

    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    const promise = requestFn();
    this.pendingRequests.set(cacheKey, promise);

    try {
      const data = await promise;
      this.cache.set(cacheKey, { data, expiry: Date.now() + this.cacheTTL });
      return data;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  async getScenarioDetails(scenarioId) {
    const language = this._getActiveLanguage();
    const cacheKey = `getScenarioDetails:${scenarioId}:${language}`;
    return this._makeRequest(cacheKey, async () => {
      const hangoutConfig = storage.get('currentHangoutConfig') || {};
      const isPrivateScenario = hangoutConfig.isPrivateScenario || false;

      if (isPrivateScenario) {
        const session = await getSession();
        if (!session?.access_token) {
          throw new Error('Authentication required for private scenario access');
        }
        const s = await fetchCache.fetchJSON(
          `${WORKER_URL}/private/scenario/details?scenario_id=${scenarioId}&language=${encodeURIComponent(language)}`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
        const scenarioData = await this._normalizeScenarioDetails(s, s.loc_id ?? s.loc ?? null);
        scenarioData.visibility = 'private';
        this._upsertScenarioInState(scenarioData);
        return scenarioData;
      }

      const s = await fetchCache.fetchPublicScenarioDetails(scenarioId, language);
      const scenarioData = await this._normalizeScenarioDetails(s, s.loc || null);
      this._upsertScenarioInState(scenarioData);
      return scenarioData;
    });
  }

  async _fetchScenarios() {
    const lang = this._getActiveLanguage();
    try {
      const scenarios = await fetchCache.fetchScenarios(lang);
      const list = Array.isArray(scenarios) ? scenarios : (scenarios?.scenarios || []);
      return filterScenariosForSfw(list).map(s => ({
        ...s,
        tags: s.tags || [],
        time: s.time
      }));
    } catch {
      const fallback = await fetchCache.fetchScenarios('en');
      const list = Array.isArray(fallback) ? fallback : (fallback?.scenarios || []);
      return filterScenariosForSfw(list);
    }
  }

  handleMappingsUpdated(event) {
    const { language } = event.detail || {};
    if (!language) return;

    for (const key of [...this.cache.keys()]) {
      if (key.startsWith('getCharacterDetails:') || key.startsWith('getScenarioDetails:')) {
        this.cache.delete(key);
      }
    }
  }
}
